import { Injectable, ApplicationRef } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, first, concat, interval } from 'rxjs';

/**
 * PWA Service
 * Handles Progressive Web App functionality including:
 * - Service worker updates
 * - Install prompt
 * - Update notifications
 */
@Injectable({
    providedIn: 'root',
})
export class PwaService {
    private promptEvent: any;

    constructor(
        private swUpdate: SwUpdate,
        private appRef: ApplicationRef,
    ) {
        this.initUpdateChecker();
        this.initInstallPromptListener();
    }

    /**
     * Initialize automatic update checker
     * Checks for updates every 6 hours after app stabilizes
     */
    private initUpdateChecker(): void {
        if (!this.swUpdate.isEnabled) {
            console.log('[PWA] Service Worker is not enabled');
            return;
        }

        // Check for updates when app stabilizes, then every 6 hours
        const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable === true));
        const everySixHours$ = interval(6 * 60 * 60 * 1000); // 6 hours
        const everySixHoursOnceAppIsStable$ = concat(appIsStable$, everySixHours$);

        everySixHoursOnceAppIsStable$.subscribe(async () => {
            try {
                const updateAvailable = await this.swUpdate.checkForUpdate();
                if (updateAvailable) {
                    console.log('[PWA] Update available');
                }
            } catch (err) {
                console.error('[PWA] Failed to check for updates:', err);
            }
        });

        // Listen for version updates
        this.swUpdate.versionUpdates
            .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
            .subscribe((evt) => {
                console.log('[PWA] New version available:', evt.latestVersion);
                this.notifyUserOfUpdate();
            });
    }

    /**
     * Listen for beforeinstallprompt event to show custom install UI
     */
    private initInstallPromptListener(): void {
        window.addEventListener('beforeinstallprompt', (e) => {
            console.log('[PWA] Install prompt available');
            e.preventDefault();
            this.promptEvent = e;
        });
    }

    /**
     * Check if app can be installed
     */
    canInstall(): boolean {
        return !!this.promptEvent;
    }

    /**
     * Show native install prompt
     */
    async showInstallPrompt(): Promise<boolean> {
        if (!this.promptEvent) {
            console.log('[PWA] Install prompt not available');
            return false;
        }

        try {
            this.promptEvent.prompt();
            const result = await this.promptEvent.userChoice;
            console.log('[PWA] Install prompt result:', result.outcome);

            if (result.outcome === 'accepted') {
                this.promptEvent = null;
                return true;
            }
            return false;
        } catch (err) {
            console.error('[PWA] Error showing install prompt:', err);
            return false;
        }
    }

    /**
     * Check if app is already installed
     */
    isInstalled(): boolean {
        return (
            window.matchMedia('(display-mode: standalone)').matches ||
            (window.navigator as any).standalone === true
        );
    }

    /**
     * Notify user of available update
     */
    private notifyUserOfUpdate(): void {
        const shouldUpdate = confirm(
            'A new version of VEO Dashboard is available. Would you like to update now?',
        );

        if (shouldUpdate) {
            this.activateUpdate();
        }
    }

    /**
     * Activate pending update and reload
     */
    async activateUpdate(): Promise<void> {
        if (!this.swUpdate.isEnabled) {
            return;
        }

        try {
            await this.swUpdate.activateUpdate();
            console.log('[PWA] Update activated, reloading...');
            window.location.reload();
        } catch (err) {
            console.error('[PWA] Failed to activate update:', err);
        }
    }

    /**
     * Unregister service worker (for debugging)
     */
    async unregister(): Promise<boolean> {
        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (const registration of registrations) {
                await registration.unregister();
            }
            console.log('[PWA] Service workers unregistered');
            return true;
        }
        return false;
    }
}
